<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="contact-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="contact" :query="{ contact: id }">
              <template #bottom>
                <ButtonComponent
                  v-if="!isNew && $store.getters.hasAccess('admin')"
                  :label="$t('delete')"
                  @click="removeDialog = true"
                  :icon="['fal', 'times']"
                />
              </template>
            </Actions>

            <ButtonComponent
              :label="isNew ? $t('create') : $t('save')"
              theme="primary"
              type="submit"
              :promise="promises.mutate"
              :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
              :class="{ 'elder-button--disabled': !validator.passes || !hasChanges }"
              v-tooltip="{ content: hasChanges ? null : $t('noUnsavedChanges') }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <Card tag="form" @submit.prevent="submit" class="contact-mutate__form">
        <template #header>
          <Avatar :value="item.avatar" size="large" />
        </template>
        <template #default>
          <div class="contact-mutate__inputs">
            <InputComponent
              v-model="data.name"
              data-field="name"
              :label="labels.name"
              required
              :is-valid="validator.fields.name.valid"
              :validation-message="validator.fields.name.error"
            />

            <InputComponent
              v-model="data.email"
              data-field="email"
              :label="labels.email"
              type="email"
              required
              :is-valid="validator.fields.email.valid"
              :validation-message="validator.fields.email.error"
              :icon="['fal', 'envelope']"
            />

            <InputComponent
              v-model="data.phone"
              data-field="phone"
              :label="labels.phone"
              type="tel"
              :is-valid="validator.fields.phone.valid"
              :validation-message="validator.fields.phone.error"
              :icon="['fal', 'mobile']"
            />

            <InputComponent
              v-model="data.birthdate"
              data-field="birthdate"
              :label="labels.birthdate"
              :icon="['fal', 'calendar']"
            >
              <InputDate v-model="birthdate" />
            </InputComponent>

            <InputComponent data-field="gender" type="select" :label="labels.gender">
              <SelectComponent
                v-model="data.gender"
                :placeholder="$t('select') + '...'"
                :items="[
                  { value: 'male', label: $t('male') },
                  { value: 'female', label: $t('female') },
                ]"
              />
            </InputComponent>

            <Tags
              data-field="tags"
              :label="$tc('tag', 2)"
              v-model="data.tags"
              :placeholder="$tc('add')"
              autocomplete="/api/contact/tags"
            />

            <CheckboxComponent v-model="isCompany" :label="labels.company" />
            <CheckboxComponent v-model="data.upsell" :label="labels.upsell" />

            <Company v-if="data.company" v-model="data.company" :validator="validator" validator-prefix="company" />

            <Address
              v-model="data.address"
              :show-map="false"
              data-field="address"
              validator-prefix="address"
              :validator="validator"
              class="my-2"
            />

            <InputComponent :label="labels.assignees" data-field="assignees">
              <UserSelect
                v-model="data.assignees"
                multiple
                search
                :items="fetchUsers"
                :title="$t('selectx', { x: labels.assignees.toLowerCase() })"
                class="elder-input__element"
            /></InputComponent>
          </div>
        </template>
      </Card>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'contacts',
          })
        "
        :resource="$tc('contact')"
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { DateTransformer } from '@/utils/transformers'
import { mapi18n } from '@/i18n'
import Model from '@/models/Contact'
import CompanyModel from '@/models/Company'
import UserApi from '@/api/User'

import UserSelect from '@/components/User/Select/Modal'
import SelectComponent from '@/components/Select/Select'
import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Address from '@/components/Address'
import RemoveComponent from '@/components/Remove'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import Tags from '@/components/Tags'
import Actions from '@/components/Actions'
import Company from '@/components/Company'
import Avatar from '@/components/Avatar'
import InputDate from '@/components/InputDate'

export default {
  mixins: [
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      guard: true,
      model: Model,
      transform: {
        assignees: v => v.map(e => e.id),
      },
    }),
  ],
  props: {
    id: String,
    project: String,
  },
  data() {
    return {
      removeDialog: false,
    }
  },
  watch: {
    id: {
      handler(id = '') {
        return this.fetch({ id })
      },
      immediate: true,
    },
    item: {
      handler(value) {
        this.data = Model(value)
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Contact', ['item', 'promises']),
    ...mapState(['enums']),
    ...mapi18n('labels', [
      'email',
      'name',
      'phone',
      'company',
      'birthdate',
      'gender',
      { upsell: 'contactUpsell' },
      { assignees: ['customermanager', 2] },
      { 'address.street': 'street' },
      { 'address.city': 'city' },
      { 'address.postcode': 'postcode' },
      { 'address.county': 'county' },
      { 'company.name': 'companyName' },
      { 'company.organizationNumber': 'orgNo' },
    ]),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingLead() {
        return this.$t('loading', { resource: this.$tc('contact', 1) }).toLowerCase() + '...'
      },
    }),
    isNew() {
      return !this.id
    },
    rules() {
      let base = {
        name: 'required',
        email:
          'required|email' +
          (this.isNew
            ? `|emailAvailable:contact:${[this.data.email, this.$path('data.company.organizationNumber')].join(':')}`
            : ''),
        phone: 'mobile',
        'address.street': 'string',
        'address.city': 'string',
        'address.postcode': 'string',
        'address.county': 'string',
      }

      if (Boolean(this.data.company))
        return {
          ...base,
          'company.name': 'required',
          'company.organizationNumber': 'required',
        }

      return base
    },
    breadcrumbs() {
      return [
        { title: this.$tc('contact', 2), action: { name: 'contacts' } },
        { title: this.$metaInfo.title, action: { name: 'contact-dashboard', params: { id: this.id } } },
        { title: this.$t('edit') },
      ]
    },
    birthdate: DateTransformer('data.birthdate'),
    isCompany: {
      get() {
        return Boolean(this.data.company)
      },
      set(val) {
        this.$set(this.data, 'company', val ? CompanyModel() : null)
      },
    },
  },
  methods: {
    ...mapActions('Contact', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Contact', ['set']),
    submit() {
      if (!this.validator.passes || !this.hasChanges) return

      let data = this.getData({ diff: true })
      if (this.isNew) data.project = this.project
      this.mutate({ id: this.id, ...data }).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'contact-edit',
          params: { id: data.id },
        })
      })
    },
    fetchUsers(filter) {
      filter.type = 'user'

      return UserApi.get({ filter }, { query: 'UserSearch' })
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createContact') : this.item.name || this.i18n.loadingContact,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    CheckboxComponent,
    RemoveComponent,
    Card,
    UserSelect,
    Tags,
    Actions,
    Company,
    Address,
    Avatar,
    InputDate,
    SelectComponent,
  },
}
</script>

<style lang="scss">
.contact-mutate {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 850px;
  }

  &__thumbnail {
    $size: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    flex-shrink: 0;
    width: $size;
    height: $size;
    background-size: cover;
    background-position: center;
    background-color: css-alpha('border-color', 0.4);
    border-radius: 50%;
    color: css-darken('border-color', 20%);
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
    align-items: flex-start;

    .company,
    [data-field='address'],
    [data-field='assignees'] {
      grid-column-end: span 2;
    }

    .company {
      margin-top: 1.5rem;
    }
  }

  [type='submit'] {
    margin-right: auto;
  }
}
</style>
